import { create } from 'zustand';
import type { LoginRequest, RegisterRequest, UserSafe } from '@network-verify/shared';
import * as authApi from '../api/auth';
import { AUTH_EVENTS } from '../api/client';
import {
  getAccessToken,
  getRefreshToken,
  getUser,
  setTokens as persistTokens,
  setUser as persistUser,
  clearAll,
} from '../utils/token';

interface AuthState {
  /** 当前用户（脱敏视图） */
  user: UserSafe | null;
  access_token: string | null;
  refresh_token: string | null;
  /** 是否已登录（依据 access_token 是否存在） */
  isAuthenticated: boolean;
  setUser: (user: UserSafe | null) => void;
  setTokens: (access_token: string, refresh_token: string) => void;
  /** 登录：拉取 token 对 + 当前用户信息 */
  login: (creds: LoginRequest) => Promise<UserSafe>;
  /** 注册：返回新用户，不自动登录 */
  register: (req: RegisterRequest) => Promise<UserSafe>;
  /** 拉取并刷新当前用户信息 */
  fetchMe: () => Promise<UserSafe>;
  /** 登出：通知后端 + 清空本地状态 */
  logout: () => Promise<void>;
  /** 仅清空本地状态（不调后端，用于 401 失败等场景） */
  reset: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: getUser(),
  access_token: getAccessToken(),
  refresh_token: getRefreshToken(),
  isAuthenticated: !!getAccessToken(),

  setUser: (user) => {
    if (user) persistUser(user);
    set({ user });
  },

  setTokens: (access_token, refresh_token) => {
    persistTokens(access_token, refresh_token);
    set({ access_token, refresh_token, isAuthenticated: true });
  },

  login: async (creds) => {
    const tokens = await authApi.login(creds);
    get().setTokens(tokens.access_token, tokens.refresh_token);
    const user = await get().fetchMe();
    return user;
  },

  register: async (req) => {
    return authApi.register(req);
  },

  fetchMe: async () => {
    const user = await authApi.me();
    get().setUser(user);
    return user;
  },

  logout: async () => {
    const { refresh_token } = get();
    try {
      await authApi.logout(refresh_token ?? undefined);
    } catch {
      // 即使后端登出失败也继续清空本地状态
    } finally {
      clearAll();
      set({ user: null, access_token: null, refresh_token: null, isAuthenticated: false });
    }
  },

  reset: () => {
    clearAll();
    set({ user: null, access_token: null, refresh_token: null, isAuthenticated: false });
  },
}));

// 监听 axios 客户端的鉴权状态变更，同步内存 store
if (typeof window !== 'undefined') {
  window.addEventListener(
    AUTH_EVENTS.TOKEN_REFRESHED,
    ((e: CustomEvent) => {
      const detail = e.detail as { access_token: string; refresh_token: string };
      useAuthStore.setState({
        access_token: detail.access_token,
        refresh_token: detail.refresh_token,
        isAuthenticated: true,
      });
    }) as EventListener,
  );
  window.addEventListener(AUTH_EVENTS.AUTH_CLEARED, () => {
    useAuthStore.setState({
      user: null,
      access_token: null,
      refresh_token: null,
      isAuthenticated: false,
    });
  });
}
